import { Auth } from '../../../shared/auth/auth.service';
import { Injectable } from '@angular/core';
import { CanActivate, UrlTree, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap, map } from 'rxjs/operators';
import { MemoizedSelector, Store } from '@ngrx/store';
import { ImUser } from '../../../shared/services/im-models/im-user';
import { getCurrentUser, getCurrentUserLoadedStatus, getCurrentUserUpdatedStatus } from '../../../store/selectors/current-user-selectors';
import { LoadCurrentUser, UpdateCurrentUser } from '../../../store/actions/current-user-actions';
import { SessionStorageService } from '../../../shared/services/web-storage/session-storage/session-storage.service';

@Injectable()
export class EntityGuard implements CanActivate {
  constructor (
    private store: Store<any>,
    private auth: Auth,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private ImUser: ImUser) {}

  canActivate (routeSnapshot: ActivatedRouteSnapshot): Observable<UrlTree|boolean> {
    return this.upsertUserAndGetStream$(getCurrentUserLoadedStatus, LoadCurrentUser).pipe(
      switchMap((user) => {
        const isEntityUser = this.ImUser.isEntityUser(user);
        if (!isEntityUser) return of(this.router.createUrlTree(['not-found']));
        const userEntities = this.ImUser.getUserEntities(user);
        const { entity: entityId } = routeSnapshot.params;
        const userHasAccessToEntity = userEntities.some((e) => e.entityId === entityId);
        if (userHasAccessToEntity) {
          this.sessionStorageService.setItem('entityId', entityId);
          const userNeedsUpdate = !this.ImUser.getCurrentEntity(user)?.entityMetadata;
          return userNeedsUpdate
            ? this.upsertUserAndGetStream$(getCurrentUserUpdatedStatus, UpdateCurrentUser).pipe(map(() => true))
            : of(true);
        } else {
          return of(this.router.createUrlTree(['not-found']));
        }
      }),
      catchError(() => of(false)),
    );
  };

  upsertUserAndGetStream$ (upsertStatusSelector: MemoizedSelector<object, boolean>, Action: typeof LoadCurrentUser | typeof UpdateCurrentUser): Observable<any> {
    return this.store.select(upsertStatusSelector).pipe(
      tap(async upserted => {
        if (!upserted) {
          const email = await this.auth.getCurrentUserEmail();
          this.store.dispatch(new Action({ email }));
        }
      }),
      filter(upserted => upserted),
      switchMap(() => this.store.select(getCurrentUser)),
      take(1),
    );
  }
}
